import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resolveAnchors } from './canvasAnchors';
import './TutorialClickPointer.css';

/**
 * Animated "tap here" marker for the guided tutorial.
 *
 * A step can pin markers to the design itself with `railPaths`, `splitShapes`
 * or `designPoints`, which survive panning, zooming and any window size. Failing
 * that a single marker sits at the centre of the highlighted control. Markers
 * are consumed as the learner clicks, so a multi-click step always shows what's
 * next.
 */
const TutorialClickPointer = ({ stepData, targetElement, clickSelector, onCountChange, onActivePointChange }) => {
  const [spots, setSpots] = useState(null);
  const [done, setDone] = useState(0);
  const previous = useRef('');

  const anchored = !!(
    stepData?.railPaths
    || stepData?.designPoints
    || stepData?.guideRungs
    || stepData?.splitShapes
  );

  useEffect(() => {
    setDone(0);
  }, [stepData?.id]);

  // Anchored markers are re-projected continuously: the design moves under the
  // learner as they pan and zoom, and a marker that lags is worse than none.
  useEffect(() => {
    if (!anchored) return undefined;

    let frame = 0;

    const update = (search) => {
      const next = resolveAnchors(stepData, { search });
      const key = JSON.stringify(next);

      if (key !== previous.current) {
        previous.current = key;
        setSpots(next);
      }
    };

    const track = () => {
      update(false);
      frame = requestAnimationFrame(track);
    };

    update(true);
    track();

    // Frames stop in a backgrounded tab, and the design the marker is pinned to
    // can appear during that gap. A slow timer means the marker still turns up
    // when the learner comes back rather than waiting for something to move.
    // This is also where the reachable spot is re-checked, which is too costly
    // to redo every frame.
    const poll = setInterval(() => update(true), 250);

    return () => {
      cancelAnimationFrame(frame);
      clearInterval(poll);
    };
  }, [anchored, stepData]);

  // Unanchored steps fall back to the centre of the highlighted control.
  useEffect(() => {
    if (anchored) return undefined;

    if (!targetElement) {
      setSpots(null);
      return undefined;
    }

    const measure = () => {
      const rect = targetElement.getBoundingClientRect();
      if (!rect.width) return;
      setSpots([{ left: rect.left + rect.width / 2, top: rect.top + rect.height / 2 }]);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(targetElement);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchored, targetElement]);

  // Retire a marker once its click lands. Counting on the control that actually
  // does something — a satin path rather than the whole canvas — means a near
  // miss leaves the marker up instead of silently moving the tutorial on.
  useEffect(() => {
    if (!clickSelector) return undefined;

    const onClick = (event) => {
      const node = event.target;
      if (!node || typeof node.closest !== 'function') return;
      if (!node.closest(clickSelector)) return;
      setDone((n) => n + 1);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [clickSelector]);

  // The step advances on the clicks it marked, so how many there are is the
  // step's business too — a marker dropped for being unreachable would
  // otherwise leave the learner one click short of moving on.
  useEffect(() => {
    if (onCountChange) onCountChange(spots ? spots.length : 0);
  }, [spots, onCountChange]);

  const pairs = !!stepData?.clickPairs;
  const remaining = useMemo(() => (spots || []).slice(done), [spots, done]);

  const activePoint = remaining[0];
  const pairedPoint = pairs && done % 2 === 0 ? remaining[1] : null;
  const activeTop = activePoint ? Math.min(activePoint.top, pairedPoint?.top ?? activePoint.top) : null;
  const activeBottom = activePoint ? Math.max(activePoint.top, pairedPoint?.top ?? activePoint.top) : null;
  useEffect(() => {
    onActivePointChange?.(activeTop === null ? null : { top: activeTop, bottom: activeBottom });
  }, [activeTop, activeBottom, onActivePointChange]);
  useEffect(() => () => onActivePointChange?.(null), [onActivePointChange]);

  if (!remaining.length) return null;

  const active = remaining[0];
  // For a paired gesture the second point is where the line ends, so show it
  // alongside the first with a connector between them.
  const partner = pairs && done % 2 === 0 && remaining[1] ? remaining[1] : null;
  const numbered = (spots?.length || 0) > 1;

  return (
    <div className="tutorial-click-layer">
      {partner && (
        <svg className="tutorial-click-connector">
          <line x1={active.left} y1={active.top} x2={partner.left} y2={partner.top} />
        </svg>
      )}

      {partner && (
        <div
          className="tutorial-click-point is-partner"
          style={{ left: partner.left, top: partner.top }}
        >
          <span className="tutorial-click-dot" />
          {numbered && <span className="tutorial-click-index">{done + 2}</span>}
        </div>
      )}

      <div
        className={`tutorial-click-point is-active ${numbered ? 'is-numbered' : ''}`}
        style={{ left: active.left, top: active.top }}
      >
        <span className="tutorial-click-ring" />
        <span className="tutorial-click-ring tutorial-click-ring--delayed" />
        <span className="tutorial-click-dot" />
        {numbered && <span className="tutorial-click-index">{done + 1}</span>}

        <svg className="tutorial-click-cursor" viewBox="0 0 24 30" width="26" height="32">
          <path
            d="M4 2 L4 20 L8.5 16.5 L11.5 24 L15 22.5 L12 15.5 L18 15 Z"
            fill="#FFFFFF"
            stroke="#12061F"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};

export default TutorialClickPointer;
